const DEFAULT_TIME_ZONE = 'America/Sao_Paulo';
const DAY_MS = 86400000;
const formatterCache = new Map();
const initializedSections = new WeakMap();

function formatter(timeZone) {
  if (!formatterCache.has(timeZone)) {
    formatterCache.set(timeZone, new Intl.DateTimeFormat('en-CA', {
      timeZone, calendar: 'gregory', numberingSystem: 'latn',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }));
  }
  return formatterCache.get(timeZone);
}

function utcValue(parts) {
  const date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  date.setUTCHours(parts.hour || 0, parts.minute || 0, parts.second || 0, parts.millisecond || 0);
  return date.getTime();
}

function validParts(parts) {
  if (parts.year < 1 || parts.year > 9999 || parts.month < 1 || parts.month > 12 || parts.day < 1 || parts.day > 31 || parts.hour < 0 || parts.hour > 23 || parts.minute < 0 || parts.minute > 59 || parts.second < 0 || parts.second > 59) return false;
  const date = new Date(utcValue(parts));
  return date.getUTCFullYear() === parts.year && date.getUTCMonth() + 1 === parts.month && date.getUTCDate() === parts.day;
}

function zonedParts(date, timeZone) {
  const parts = {};
  for (const part of formatter(timeZone).formatToParts(date)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  parts.millisecond = date.getUTCMilliseconds();
  return parts;
}

function localInstant(parts, timeZone) {
  const localValue = utcValue(parts);
  const offsets = new Set();
  for (const delta of [-2 * DAY_MS, -DAY_MS, 0, DAY_MS, 2 * DAY_MS]) {
    const sample = new Date(localValue + delta);
    offsets.add(utcValue(zonedParts(sample, timeZone)) - sample.getTime());
  }
  const candidates = [];
  for (const offset of offsets) {
    const candidate = new Date(localValue - offset);
    const actual = zonedParts(candidate, timeZone);
    if (['year', 'month', 'day', 'hour', 'minute', 'second', 'millisecond'].every(key => actual[key] === parts[key])) candidates.push(candidate);
  }
  if (!candidates.length) throw new RangeError('Tracking date does not exist in the configured time zone.');
  return candidates.sort((a, b) => a.getTime() - b.getTime())[0];
}

export function parseTrackingDate(value, timeZone = DEFAULT_TIME_ZONE) {
  try {
    formatter(timeZone);
    if (value instanceof Date) return Number.isFinite(value.getTime()) ? new Date(value.getTime()) : null;
    if (typeof value === 'number') {
      const date = new Date(value);
      return Number.isFinite(date.getTime()) ? date : null;
    }
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:?\d{2})?)?$/i);
    if (!match) return null;
    const parts = {
      year: Number(match[1]), month: Number(match[2]), day: Number(match[3]),
      hour: match[4] === undefined ? 9 : Number(match[4]), minute: Number(match[5] || 0),
      second: Number(match[6] || 0), millisecond: Number((match[7] || '').padEnd(3, '0'))
    };
    if (!validParts(parts)) return null;
    if (!match[8]) return localInstant(parts, timeZone);
    let offset = 0;
    if (match[8].toUpperCase() !== 'Z') {
      const offsetMatch = match[8].match(/^([+-])(\d{2}):?(\d{2})$/);
      const hours = Number(offsetMatch[2]);
      const minutes = Number(offsetMatch[3]);
      if (hours > 14 || minutes > 59 || (hours === 14 && minutes !== 0)) return null;
      offset = (hours * 60 + minutes) * (offsetMatch[1] === '+' ? 1 : -1);
    }
    const date = new Date(utcValue(parts) - offset * 60000);
    return Number.isFinite(date.getTime()) ? date : null;
  } catch {
    return null;
  }
}

function requiredDate(value, timeZone, name) {
  const date = parseTrackingDate(value, timeZone);
  if (!date) throw new RangeError(`Invalid tracking ${name}.`);
  return date;
}

function optionalDate(value, timeZone, name) {
  return value === null || value === undefined || value === '' ? null : requiredDate(value, timeZone, name);
}

function dateKey(parts) {
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

function holidaySet(values, timeZone) {
  const entries = Array.isArray(values) ? values : String(values || '').split(/[\s,]+/);
  const result = new Set();
  for (const entry of entries) {
    if (!entry) continue;
    if (typeof entry !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(entry) || !parseTrackingDate(entry, timeZone)) throw new RangeError('Invalid tracking holiday.');
    result.add(entry);
  }
  return result;
}

function addBusinessDays(anchor, count, holidays, timeZone) {
  const original = zonedParts(anchor, timeZone);
  const calendar = new Date(utcValue({ ...original, hour: 12, minute: 0, second: 0, millisecond: 0 }));
  let added = 0;
  while (added < count) {
    calendar.setUTCDate(calendar.getUTCDate() + 1);
    const day = { year: calendar.getUTCFullYear(), month: calendar.getUTCMonth() + 1, day: calendar.getUTCDate() };
    const weekday = calendar.getUTCDay();
    if (weekday !== 0 && weekday !== 6 && !holidays.has(dateKey(day))) added += 1;
  }
  return localInstant({ ...original, year: calendar.getUTCFullYear(), month: calendar.getUTCMonth() + 1, day: calendar.getUTCDate() }, timeZone);
}

export function calculateTrackingSchedule(input = {}) {
  const timeZone = input.timeZone || DEFAULT_TIME_ZONE;
  formatter(timeZone);
  const createdAt = requiredDate(input.createdAt, timeZone, 'createdAt');
  const now = input.now === undefined ? new Date() : requiredDate(input.now, timeZone, 'now');
  const transportAt = optionalDate(input.transportAt, timeZone, 'transportAt');
  if (transportAt && transportAt.getTime() > now.getTime()) throw new RangeError('Tracking transportAt cannot be in the future.');
  const pauseAt = optionalDate(input.pauseAt, timeZone, 'pauseAt');
  const stopAt = optionalDate(input.stopAt, timeZone, 'stopAt');
  const holidays = holidaySet(input.holidays, timeZone);
  const confirmedStage = Number(input.confirmedStage ?? 0);
  if (!Number.isInteger(confirmedStage) || confirmedStage < 0 || confirmedStage > 6) throw new RangeError('Invalid tracking confirmedStage.');
  const state = input.cancelled ? 'cancelled' : input.refunded ? 'refunded' : input.delivered ? 'delivered' : input.stopped || stopAt ? 'stopped' : input.paused || pauseAt ? 'paused' : 'active';
  const cutoffs = [];
  if (state !== 'active' && stopAt) cutoffs.push(stopAt);
  if (state !== 'active' && pauseAt) cutoffs.push(pauseAt);
  const hasKnownCutoff = cutoffs.length > 0;
  const cutoff = new Date(Math.min(now.getTime(), ...cutoffs.map(date => date.getTime())));
  const mayProject = input.paid === true && input.estimatedEnabled !== false && (state === 'active' || hasKnownCutoff);
  const preparationMilestones = [];
  if (mayProject) {
    for (let businessDays = 1; businessDays <= 4; businessDays += 1) {
      preparationMilestones.push({ at: addBusinessDays(createdAt, businessDays, holidays, timeZone).toISOString(), stageIndex: businessDays + 1, type: 'preparation', businessDays });
    }
  }
  const transportMilestones = [];
  if (input.imported && transportAt && (state === 'active' || hasKnownCutoff)) {
    for (const businessDays of [2, 4, 6]) {
      transportMilestones.push({ at: addBusinessDays(transportAt, businessDays, holidays, timeZone).toISOString(), stageIndex: 6, type: 'international', businessDays });
    }
  }
  const milestones = [...preparationMilestones, ...transportMilestones].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const dueMilestones = milestones.filter(milestone => Date.parse(milestone.at) <= cutoff.getTime());
  const lastMilestone = dueMilestones.at(-1) || null;
  const lastPreparation = dueMilestones.filter(milestone => milestone.type === 'preparation').at(-1) || null;
  const realStage = input.delivered || transportAt ? 6 : Math.max(confirmedStage, input.paid ? 1 : 0);
  const futureMilestones = milestones.filter(milestone => Date.parse(milestone.at) > now.getTime() && (milestone.type === 'international' ? realStage === 6 : milestone.stageIndex > realStage));
  const nextAt = state === 'active' ? futureMilestones[0]?.at || null : null;
  return {
    state, confirmedStage: realStage, createdAt: createdAt.toISOString(), now: now.toISOString(),
    cutoffAt: state === 'active' ? null : hasKnownCutoff ? cutoff.toISOString() : null,
    milestones, dueMilestones, lastMilestone, lastPreparation, nextAt,
    preparationComplete: Boolean(lastPreparation && lastPreparation.stageIndex === 5 && realStage < 6),
    projectionEligible: mayProject
  };
}

export function calculateTrackingProgress(input = {}) {
  const schedule = calculateTrackingSchedule(input);
  const projectedStage = schedule.lastPreparation?.stageIndex ?? 0;
  const stageIndex = Math.max(schedule.confirmedStage, projectedStage);
  return { ...schedule, stageIndex, estimated: stageIndex > schedule.confirmedStage };
}

function replaceTokens(template, values) {
  return Object.entries(values).reduce((text, [key, value]) => String(text).replaceAll(`{{${key}}}`, value).replaceAll(`{{ ${key} }}`, value).replaceAll(`%${key}%`, value), template || '');
}

function writeText(root, selector, value) {
  const node = root.querySelector(selector);
  if (node && node.textContent !== value) node.textContent = value;
  return node;
}

function setVisible(node, visible) {
  if (node) node.hidden = !visible;
}

function humanDate(iso, timeZone) {
  return new Intl.DateTimeFormat('pt-BR', { timeZone, day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));
}

function createOrderController(card, options) {
  const payload = JSON.parse(card.querySelector('[data-tracking-data]')?.textContent || '{}');
  const order = payload.order || payload;
  const config = payload.config || {};
  const stages = payload.stages || order.stages;
  const strings = payload.strings || order.strings || {};
  if (!Array.isArray(stages) || stages.length !== 7 || stages.some(stage => typeof stage.title !== 'string' || typeof stage.description !== 'string')) throw new TypeError('Invalid tracking stages.');
  let selected = null;
  let explored = false;
  let lastAnnouncement = null;
  let progress = { state: 'active' };
  const buttons = Array.from(card.querySelectorAll('[data-tracking-step]'));

  function updateDetail() {
    const stateTitle = strings[`${progress.state}Title`];
    const stateDescription = strings[`${progress.state}Description`];
    const useState = !explored && progress.state !== 'active' && stateTitle;
    writeText(card, '[data-step-title]', useState ? stateTitle : stages[selected].title);
    writeText(card, '[data-step-description]', useState ? stateDescription || stages[selected].description : stages[selected].description);
    for (const button of buttons) {
      const index = Number(button.dataset.trackingStep);
      button.setAttribute('aria-pressed', String(index === selected));
      button.classList.toggle('is-explored', index === selected);
    }
  }

  const clickHandlers = buttons.map(button => {
    const handler = () => {
      const index = Number(button.dataset.trackingStep);
      if (!Number.isInteger(index) || index < 0 || index > 6) return;
      explored = true;
      selected = index;
      updateDetail();
    };
    button.addEventListener('click', handler);
    return [button, handler];
  });

  function render() {
    progress = calculateTrackingProgress({ ...order, ...config, now: options.now() });
    if (!explored || selected === null) selected = progress.stageIndex;
    const currentTitle = progress.state !== 'active' && strings[`${progress.state}Title`] ? strings[`${progress.state}Title`] : stages[progress.stageIndex].title;
    const currentLabel = progress.estimated ? strings.estimated : strings.confirmed;
    writeText(card, '[data-current-label]', currentLabel || '');
    writeText(card, '[data-current-title]', currentTitle);
    writeText(card, '[data-current-number]', String(progress.stageIndex + 1).padStart(2, '0'));
    writeText(card, '[data-summary-stage]', currentTitle);
    writeText(card, '[data-summary-label]', progress.estimated ? strings.estimated || '' : strings.currentStage || strings.confirmed || '');
    card.dataset.trackingState = progress.state;
    card.dataset.trackingEstimated = String(progress.estimated);
    for (const button of buttons) {
      const index = Number(button.dataset.trackingStep);
      const current = index === progress.stageIndex;
      button.classList.toggle('is-current', current);
      button.classList.toggle('is-complete', index < progress.stageIndex);
      button.classList.toggle('is-estimated', current && progress.estimated);
      if (current) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
      const status = index <= progress.confirmedStage ? strings.confirmed : index <= progress.stageIndex ? strings.estimated : '';
      writeText(button, '[data-step-status]', status || '');
      button.setAttribute('aria-label', `${String(index + 1).padStart(2, '0')} — ${stages[index].title}${status ? `. ${status}` : ''}`);
    }
    const percentage = (progress.stageIndex / 6) * 100;
    const bar = card.querySelector('[data-tracking-progress]');
    if (bar) {
      bar.style.width = `${percentage}%`;
      bar.setAttribute('aria-valuenow', String(progress.stageIndex + 1));
      bar.setAttribute('aria-valuetext', `${currentTitle}. ${currentLabel || ''}`);
    }
    card.style.setProperty('--tracking-progress', `${percentage}%`);
    const note = writeText(card, '[data-confirmation-note]', replaceTokens(strings.lastConfirmed, { stage: stages[progress.confirmedStage].title }));
    setVisible(note, progress.estimated);
    let nextMessage = strings.currentFallback || '';
    if (progress.state === 'paused') nextMessage = strings.pausedDescription || nextMessage;
    else if (progress.state === 'stopped') nextMessage = strings.stoppedDescription || nextMessage;
    else if (['delivered', 'cancelled', 'refunded'].includes(progress.state)) nextMessage = strings[`${progress.state}Description`] || nextMessage;
    else if (progress.nextAt) nextMessage = replaceTokens(strings.nextUpdate, { date: humanDate(progress.nextAt, config.timeZone || DEFAULT_TIME_ZONE) });
    else if (progress.preparationComplete) nextMessage = strings.preparationComplete || nextMessage;
    writeText(card, '[data-next-update]', nextMessage);
    setVisible(card.querySelector('[data-tracking-fallback]'), false);
    updateDetail();
    const announcement = `${currentLabel || ''}: ${currentTitle}`;
    if (lastAnnouncement !== null && lastAnnouncement !== announcement) writeText(card, '[data-tracking-live]', announcement);
    lastAnnouncement = announcement;
    return progress.nextAt;
  }

  return {
    render,
    fail() { setVisible(card.querySelector('[data-tracking-fallback]'), true); },
    destroy() { for (const [button, handler] of clickHandlers) button.removeEventListener('click', handler); }
  };
}

export function initializeTrackingSection(root, overrides = {}) {
  if (!root || initializedSections.has(root)) return initializedSections.get(root) || null;
  const doc = root.ownerDocument || globalThis.document;
  const win = doc?.defaultView || globalThis;
  const options = {
    now: () => new Date(), setTimeout: win.setTimeout.bind(win), clearTimeout: win.clearTimeout.bind(win), ...overrides
  };
  const controllers = [];
  for (const card of root.querySelectorAll('[data-tracking-order]')) {
    try { controllers.push(createOrderController(card, options)); }
    catch { setVisible(card.querySelector('[data-tracking-fallback]'), true); }
  }
  let timer = null;
  let destroyed = false;
  function stopTimer() {
    if (timer !== null) options.clearTimeout(timer);
    timer = null;
  }
  function update() {
    stopTimer();
    if (destroyed || doc?.hidden) return;
    const upcoming = [];
    for (const controller of controllers) {
      try {
        const nextAt = controller.render();
        if (nextAt) upcoming.push(Date.parse(nextAt));
      } catch { controller.fail(); }
    }
    if (controllers.length) {
      const nextDelay = upcoming.length ? Math.min(...upcoming) - options.now().getTime() + 2 : 60000;
      timer = options.setTimeout(update, Math.max(20, Math.min(60000, nextDelay)));
    }
  }
  function visibilityChange() { if (doc.hidden) stopTimer(); else update(); }
  function destroy() {
    destroyed = true;
    stopTimer();
    doc?.removeEventListener('visibilitychange', visibilityChange);
    win.removeEventListener?.('pageshow', update);
    root.removeEventListener('shopify:section:unload', destroy);
    for (const controller of controllers) controller.destroy();
    initializedSections.delete(root);
  }
  const instance = { update, destroy };
  initializedSections.set(root, instance);
  doc?.addEventListener('visibilitychange', visibilityChange);
  win.addEventListener?.('pageshow', update);
  root.addEventListener('shopify:section:unload', destroy);
  update();
  return instance;
}

function initializeWithin(scope) {
  if (scope.matches?.('[data-louford-tracking]')) initializeTrackingSection(scope);
  for (const root of scope.querySelectorAll?.('[data-louford-tracking]') || []) initializeTrackingSection(root);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (initializedSections.has(event.target)) initializedSections.get(event.target).destroy();
    for (const root of event.target.querySelectorAll?.('[data-louford-tracking]') || []) initializedSections.get(root)?.destroy();
  });
}
